import { notesApi } from '@/lib/notesApi';

// --- helpers ---------------------------------------------------------------
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission() {
  try { return Notification.permission; } catch (e) { return 'default'; }
}

// iOS Safari only allows Web Push when the app has been added to the Home
// Screen and launched as a standalone PWA.
export function isIOS() {
  return /iP(hone|ad|od)/i.test(navigator.userAgent || '');
}
export function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}
export function iosNeedsInstall() {
  return isIOS() && !isStandalone();
}

let swRegistration = null;
async function registerSW() {
  if (swRegistration) return swRegistration;
  swRegistration = await navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return swRegistration;
}

async function subscribeAndSave(registration) {
  const { data } = await notesApi.get('/notes/push/public-key');
  if (!data || !data.enabled || !data.publicKey) return null;
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing || (await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
  }));
  const json = subscription.toJSON();
  await notesApi.post('/notes/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    expirationTime: json.expirationTime || null,
    ua: navigator.userAgent,
  });
  return subscription;
}

/** Full opt-in flow — MUST be called from a user gesture (button click). */
export async function enablePush() {
  if (!pushSupported()) throw new Error('unsupported');
  const registration = await registerSW();
  if (Notification.permission === 'denied') throw new Error('denied');
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') return null;
  return subscribeAndSave(registration);
}

/** Silent refresh — if already granted, make sure this device is subscribed
 * (keeps the subscription fresh across logins / new devices). Never prompts. */
export async function ensurePushIfGranted() {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return null;
    const registration = await registerSW();
    return await subscribeAndSave(registration);
  } catch (e) {
    return null;
  }
}

export async function disablePush() {
  try {
    if (!pushSupported()) return;
    const registration = await registerSW();
    const sub = await registration.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      try { await notesApi.post('/notes/push/unsubscribe', { endpoint: json.endpoint, keys: json.keys || {} }); } catch (e) { /* ignore */ }
      await sub.unsubscribe();
    }
  } catch (e) { /* ignore */ }
}

/** Ask the server to fire a real push to every device this user has registered.
 * Returns { enabled, devices, sent, results } so callers can show a precise,
 * per-device diagnostic. Silently ensures a fresh subscription first. */
export async function sendTestPush() {
  try { await ensurePushIfGranted(); } catch (e) { /* ignore */ }
  const { data } = await notesApi.post('/notes/push/test', {});
  return data;
}
